import { faCheck, faMinus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styled from 'styled-components';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import { BLACK2, BORDER_GRAY, GRAY300, WHITE } from '../../../theme';
import { EventNames, EventSubCategories } from '../../../utils/trackingEvents';
import { Accordion } from '../Accordion';
import Text, { TextColor, TextKind } from '../Text';
import { SizeProp } from '../util';

const StyledCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const StyledCheckbox = styled.input<{ $size: SizeProp }>`
  appearance: none;
  ${({ $size }) => {
    switch ($size) {
      case SizeProp.Small: {
        return `
          width: 16px;
          height: 16px;
        `;
      }
      case SizeProp.Medium:
      default: {
        return `
          width: 24px;
          height: 24px;
        `;
      }
    }
  }}

  background: ${WHITE};
  border: 1px solid ${BORDER_GRAY};
  border-radius: 4px;
  margin: 0;

  &:focus {
    border-color: ${BLACK2};
  }

  &:checked {
    background: ${BLACK2};
  }
  &[data-checked='intermediate'] {
    background: ${BLACK2};
  }

  &:disabled {
    background: ${GRAY300};
  }
`;

export interface CheckboxOptionType {
  label: string;
  value: any;
  name?: string;
  parentValue?: string;
  children?: string[];
}

export interface CheckboxProps extends CheckboxOptionType {
  checked: boolean | 'intermediate';
  onChange: (value: CheckboxOptionType) => void;
  disabled?: boolean;
  size?: SizeProp;
  trackingData?: TrackingEventData;
}

export const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  onChange,
  name,
  value,
  disabled,
  size = SizeProp.Medium,
  trackingData,
  parentValue,
}) => {
  const { track } = useTracking();

  const handleChange = () => {
    if (trackingData) {
      track(EventNames.FilterClick, {
        ...trackingData,
        event_label: label,
        event_subcategory: checked
          ? EventSubCategories.Off
          : EventSubCategories.On,
      });
    }
    onChange({ name, value, label });
  };
  return (
    <StyledCheckboxLabel>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          paddingLeft: parentValue ? '24px' : '0',
        }}
      >
        <StyledCheckbox
          data-testid={`checkbox-${value}`}
          data-checked={checked}
          name={name}
          type="checkbox"
          checked={checked === 'intermediate' ? false : checked}
          disabled={disabled}
          onChange={handleChange}
          $size={size}
        />{' '}
        {typeof checked === 'boolean' && checked && (
          <FontAwesomeIcon
            aria-hidden
            icon={faCheck}
            color={WHITE}
            fontSize={
              {
                [SizeProp.Small]: '12px',
                [SizeProp.Medium]: '18px',
              }[size]
            }
            style={{ position: 'absolute' }}
          />
        )}
        {checked === 'intermediate' && (
          <FontAwesomeIcon
            aria-hidden
            icon={faMinus}
            color={WHITE}
            fontSize={
              {
                [SizeProp.Small]: '12px',
                [SizeProp.Medium]: '12px',
              }[size]
            }
            style={{ position: 'absolute' }}
          />
        )}
      </div>
      <Text
        kind={
          {
            [SizeProp.Small]: TextKind.BodyS,
            [SizeProp.Medium]: TextKind.BodyM,
          }[size]
        }
        color={TextColor.Secondary}
      >
        {label}
      </Text>
    </StyledCheckboxLabel>
  );
};

interface CheckboxGroupProps {
  label: string;
  options: CheckboxOptionType[];
  name: string;
  onChange: (value: CheckboxOptionType) => void;
  checkedOptions: CheckboxOptionType['value'][] | [];
  collapsible?: boolean;
  size?: SizeProp;
  trackingData?: TrackingEventData;
}

export const CheckboxGroup = ({
  label,
  options,
  name,
  onChange,
  checkedOptions,
  collapsible,
  size = SizeProp.Medium,
  trackingData,
}: CheckboxGroupProps) => {
  const checkBoxList = (
    <StyledCheckboxGroup>
      {options.map((option) => {
        const isChecked = checkedOptions.some((o) => o === option.value);
        const isIntermediate = checkedOptions.some((o) =>
          option.children?.includes(o),
        );
        return (
          <Checkbox
            key={option.label}
            name={name}
            label={option.label}
            value={option.value}
            onChange={onChange}
            checked={!isChecked && isIntermediate ? 'intermediate' : isChecked}
            size={size}
            parentValue={option.parentValue}
            trackingData={
              trackingData
                ? { ...trackingData, section_name: label }
                : undefined
            }
          />
        );
      })}
    </StyledCheckboxGroup>
  );

  if (collapsible) {
    return (
      <Accordion
        trackingData={{
          ...trackingData,
          section_name: label,
        }}
        name={name}
        size={SizeProp.Small}
        label={
          <Text kind={TextKind.ButtonM}>
            {label}{' '}
            {checkedOptions.length > 0 ? `(${checkedOptions.length})` : null}
          </Text>
        }
      >
        <div style={{ padding: '8px' }}>{checkBoxList}</div>
      </Accordion>
    );
  }

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
      data-testid="checkbox-group"
    >
      <Text kind={TextKind.LabelL}>{label}</Text>
      {checkBoxList}
    </div>
  );
};
